import { BookOpen, ChevronRight, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Markdown } from '@/components/ui/markdown';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { guideBySlug, visibleGuideGroups, visibleGuides } from '../registry';
import type { Guide, GuideModule } from '../types';

function TopicButton({
  guide,
  active,
  onSelect,
}: {
  guide: Guide;
  active: boolean;
  onSelect: (slug: string) => void;
}) {
  const Icon = guide.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(guide.slug)}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        active ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground',
      )}
    >
      <Icon className="size-4 shrink-0" />
      <span className="truncate">{guide.title}</span>
    </button>
  );
}

interface GuidesReaderProps {
  /** Restrict to one module (e.g. a per-module "Guides" view). Omit for all. */
  module?: GuideModule;
}

/** Docs-style reader: a grouped, searchable topic list beside the rendered guide. */
export function GuidesReader({ module }: GuidesReaderProps) {
  const role = useSession((s) => s.role);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  // Per-module open override; until the user toggles, only the first group
  // (My Space) is open and every other group is closed.
  const [overrides, setOverrides] = useState<Map<GuideModule, boolean>>(new Map());
  const setModuleOpen = (m: GuideModule, open: boolean) =>
    setOverrides((prev) => new Map(prev).set(m, open));

  const groups = useMemo(() => visibleGuideGroups(role, module), [role, module]);
  const all = useMemo(() => visibleGuides(role, module), [role, module]);

  // Active guide comes from `?guide=`; fall back to the first visible one.
  const requested = params.get('guide');
  const active =
    (requested && all.some((g) => g.slug === requested) && guideBySlug(requested)) || all[0];

  const select = (slug: string) => {
    const next = new URLSearchParams(params);
    next.set('guide', slug);
    setParams(next, { replace: true });
  };

  const q = query.trim().toLowerCase();
  const filteredGroups = q
    ? groups
        .map((g) => ({
          ...g,
          guides: g.guides.filter(
            (x) =>
              x.title.toLowerCase().includes(q) || x.summary.toLowerCase().includes(q),
          ),
        }))
        .filter((g) => g.guides.length > 0)
    : groups;

  if (!active) {
    return (
      <Card>
        <CardContent className="text-muted-foreground py-16 text-center text-sm">
          No guides are available for your role yet.
        </CardContent>
      </Card>
    );
  }

  const ActiveIcon = active.icon;
  const showGroupHeadings = !module; // a single-module view doesn't need the heading

  return (
    <div className="grid gap-6 lg:grid-cols-[16rem_1fr] lg:items-start">
      {/* Topic list */}
      <aside className="lg:bg-card lg:sticky lg:top-4 lg:rounded-lg lg:border lg:p-2">
        <div className="relative mb-2">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guides…"
            className="pl-8"
            aria-label="Search guides"
          />
        </div>
        {/* Height tracks the menu's content; only caps + scrolls when the list
            would exceed the viewport (keeps the sticky sidebar on-screen). */}
        <div className="lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
          <nav aria-label="Guides" className="space-y-3 p-1">
            {filteredGroups.length === 0 ? (
              <p className="text-muted-foreground px-2 py-6 text-center text-sm">No matches.</p>
            ) : (
              filteredGroups.map((group, index) => {
                const buttons = group.guides.map((g) => (
                  <TopicButton
                    key={g.slug}
                    guide={g}
                    active={g.slug === active.slug}
                    onSelect={select}
                  />
                ));
                if (!showGroupHeadings) {
                  return (
                    <div key={group.module} className="space-y-1">
                      {buttons}
                    </div>
                  );
                }
                // Default: only the first group (My Space) is open. The user can
                // toggle any group; a search forces all open so matches show.
                const open = Boolean(q) || (overrides.get(group.module) ?? index === 0);
                return (
                  <Collapsible
                    key={group.module}
                    open={open}
                    onOpenChange={(o) => setModuleOpen(group.module, o)}
                    className="space-y-1"
                  >
                    <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex w-full items-center gap-2 px-2 pt-1 text-xs font-semibold tracking-wide uppercase transition-colors">
                      <span className="flex-1 text-left">{group.label}</span>
                      <ChevronRight
                        className={cn(
                          'size-3.5 shrink-0 transition-transform',
                          open && 'rotate-90',
                        )}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-1">{buttons}</CollapsibleContent>
                  </Collapsible>
                );
              })
            )}
          </nav>
        </div>
      </aside>

      {/* Selected guide */}
      <Card className="min-w-0">
        <CardHeader className="gap-1.5">
          <div className="text-primary flex items-center gap-2 text-sm font-medium">
            <ActiveIcon className="size-4" />
            <BookOpen className="size-3.5 opacity-40" />
          </div>
          <CardTitle className="text-2xl">{active.title}</CardTitle>
          <p className="text-muted-foreground">{active.summary}</p>
        </CardHeader>
        <CardContent>
          <Markdown>{active.body}</Markdown>
        </CardContent>
      </Card>
    </div>
  );
}
